import React, { useState } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Button, Tooltip, Image, FileItemLayout } from '../ui';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog/DeleteConfirmationDialog';
import FileTypeIcon from '../ui/Icons/FileTypeIcons/FileTypeIcon';
import { IconDownload, IconEyeView, IconTrash } from '../ui/Icons';
import { downloadBlobAsFile } from '@helpers/utility';

const {
  ThumbnailWrapper,
  ListItemWrapper,
  ListItemInfoWrapper,
  FileInfoWrapper,
  FileSize,
  FileUploadDate,
  FileUploadDateMobile,
  FileSizeWrapper,
  ButtonsWrapper,
  IconEyeViewWrapper,
  ViewFileButtonWrapper,
  FileTypeIconButton,
  FileNameWrapper,
  FileName,
  FileExtension
} = FileItemLayout;

const FileItem = ({ disabled, viewOnly, file, onDelete, isActive, requireDeleteConfirmation = true }) => {
  const [showConfirmationModal, toggleModal] = useState(false);
  const [visible, setVisible] = useState(false);

  const { size } = file || {};
  const fileId = file?.id;
  const fileSizeInMb = (size / 1048576).toFixed(2);
  const fileMimeType = file?.mime_type || '';
  const fileExtensionLower = file?.filename?.split('.').pop()?.toLowerCase();
  const [fileMainType, fileSubType] = fileMimeType.split('/');
  const fileIsImage = fileMainType === 'image';
  const fileIsVideo = fileMainType === 'video';
  const fileIsPdf = fileMimeType === 'application/pdf' || fileExtensionLower === 'pdf';
  const fileIsSpreadsheetMime = fileMimeType.includes('spreadsheetml.sheet');
  const fileIsXlsxExtension = fileExtensionLower === 'xlsx';
  const fileIsExcel = fileIsSpreadsheetMime || fileIsXlsxExtension;
  // Preview-Button anzeigen für alles was sich im Browser anzeigen lässt
  // (Bild im Modal, PDF/Video in neuem Tab). Excel/sonstiges: nur Download.
  const fileIsPreviewable = fileIsImage || fileIsPdf || fileIsVideo;
  const fileType = fileIsSpreadsheetMime || fileIsXlsxExtension ? 'xlsx' : fileSubType;
  const fileName = file?.filename?.substring(0, file?.filename?.lastIndexOf('.'));
  const fileExtension = file?.filename?.substring(file?.filename?.lastIndexOf('.') + 1);

  const onConfirmFileDelete = (file) => {
    toggleModal(false);
    return onDelete(file);
  };

  const openFile = (file) => {
    const { url } = file || {};
    window.open(url, '_blank');
  };

  const downloadFile = async (file) => {
    const { url, filename } = file || {};
    if (!url) return;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      downloadBlobAsFile(blob, filename);
    } catch {
      window.open(url, '_blank');
    }
  };

  const handlePreview = () => {
    if (fileIsImage) {
      setVisible(true);
    } else if (fileIsExcel) {
      downloadFile(file);
    } else {
      openFile(file);
    }
  };

  const getThumbnail = () => {
    const { url, url_small } = file || {};
    const isPng = fileMimeType.includes('png');
    // not all images may have a thumbnail
    const thumbnailUrl = url_small || url;

    if (fileIsImage) {
      return (
        <Image
          src={thumbnailUrl}
          className={classNames('details-page-files-image', {
            'no-placeholder': isPng
          })}
          alt={file.filename}
          preview={{
            src: url,
            open: visible,
            onOpenChange: (value) => {
              setVisible(value);
            }
          }}
          placeholder
        />
      );
    }
    return (
      <FileTypeIconButton type="button" onClick={handlePreview}>
        <FileTypeIcon type={fileType} />
      </FileTypeIconButton>
    );
  };

  const handleOnClick = (event) => {
    event.preventDefault();
    if (disabled) return;

    if (!requireDeleteConfirmation) {
      return onDelete(file);
    }

    return toggleModal(true);
  };

  const confirmDeleteFileModal = (
    <DeleteConfirmationDialog
      onCancel={() => toggleModal(false)}
      onSubmit={() => onConfirmFileDelete(file)}
      show={showConfirmationModal}
      confirmationButtonLabel="Löschen"
      cancelButtonLabel="Abbrechen"
      title="Datei löschen"
    >
      Sind Sie sicher, dass Sie die Datei löschen wollen?
    </DeleteConfirmationDialog>
  );

  // icon-only buttons (no text labels)
  const buttonClasses = 'btn-inline btn-icon-only';

  const renderViewPreviewButton = () => {
    return (
      <ViewFileButtonWrapper>
        <Button
          aria-label="Datei anzeigen"
          className={buttonClasses}
          onClick={handlePreview}
          type="link"
          icon={
            <IconEyeViewWrapper>
              <IconEyeView />
            </IconEyeViewWrapper>
          }
        />
      </ViewFileButtonWrapper>
    );
  };

  const renderDownloadButton = () => {
    return (
      <Button
        aria-label="Datei herunterladen"
        className={buttonClasses}
        onClick={() => downloadFile(file)}
        type="link"
        icon={<IconDownload />}
      />
    );
  };

  const renderDeleteButton = () => {
    return (
      <Button
        aria-label="Datei löschen"
        disabled={disabled}
        onClick={handleOnClick}
        className={buttonClasses}
        type="link"
        icon={<IconTrash />}
        danger
      />
    );
  };

  return (
    <>
      {requireDeleteConfirmation && confirmDeleteFileModal}
      <ListItemWrapper data-id={fileId} className={classNames({ active: isActive })}>
        <ListItemInfoWrapper>
          <ThumbnailWrapper>{getThumbnail()}</ThumbnailWrapper>
          <FileInfoWrapper>
            <FileNameWrapper>
              <FileName>
                <Tooltip title={file.filename}>{`${fileName}.`}</Tooltip>
              </FileName>
              <FileExtension>{fileExtension}</FileExtension>
            </FileNameWrapper>
            <FileSizeWrapper>
              <FileSize>{`${fileSizeInMb} MB`}</FileSize>
              <FileUploadDate>
                {format(new Date(file.created_at), "'Hinzugefügt am ' dd.MM.yyyy 'um' HH:mm 'Uhr'")}
              </FileUploadDate>
              <FileUploadDateMobile>{format(new Date(file.created_at), 'dd.MM.yyyy')}</FileUploadDateMobile>
            </FileSizeWrapper>
          </FileInfoWrapper>
        </ListItemInfoWrapper>
        <ButtonsWrapper>
          {fileIsPreviewable && renderViewPreviewButton()}
          {renderDownloadButton()}
          {!viewOnly && renderDeleteButton()}
        </ButtonsWrapper>
      </ListItemWrapper>
    </>
  );
};

export default React.memo(FileItem);
